'use client';

import { useMutation, useQuery } from '@tanstack/react-query';
import qs from 'qs';

import { ICart } from '@/entities/cart/types';
import {
  addToLocalStorageCart,
  clearLocalStorageCart,
  getLocalStorageCart,
  getLocalStorageCartCount,
  removeFromLocalStorageCart,
} from '@/entities/cart/utils';
import { queryClient } from '@/shared/api/query-client';
import { QUERY_KEYS } from '@/shared/const/query-keys';
import { sendYMEvent, YM_TARGETS_ENUM } from '@/shared/const/ym';
import { useSession } from '@/shared/hooks/use-session';
import { useApi } from '@/shared/hooks/use-api';
import { useToast } from '@/components/ui/use-toast';
import { getApiUrl } from '@/shared/utils/getApiUrl';
import { apiClient } from '@/shared/api/api-client';

export const useCartQuery = ({
  promocode,
  localCartItemsIds,
}: {
  promocode?: string;
  localCartItemsIds?: string[];
}) => {
  const { token, isPending } = useSession();

  return useQuery({
    queryKey: [QUERY_KEYS.cartItems, promocode, localCartItemsIds],
    queryFn: async () => {
      return await apiClient.get(getApiUrl('/cart'), {
        params: {
          promocode,
          localCartItemsIds,
        },
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        paramsSerializer: (params) => {
          return qs.stringify(params, { arrayFormat: 'comma' });
        },
      });
    },
    select: (data): ICart => {
      return data.data;
    },
    enabled: !isPending,
  });
};

export const useAddMasterclassToCartMutation = () => {
  const api = useApi();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (masterclassId: number) => {
      return await api.post(`/cart/add/${masterclassId}`, {});
    },
    onSuccess: () => {
      sendYMEvent(YM_TARGETS_ENUM.ADD_TO_CART);
      toast({
        title: 'Мастер-класс добавлен в корзину',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.cartItems] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.masterclasses] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.masterclass] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.cartCount] });
    },
  });
};
export const useAddManyMasterclassesToCartMutation = () => {
  const api = useApi();
  // toast закомментирован ниже; хук сохраняем, чтобы не менять порядок хуков
  const { toast: _toast } = useToast();

  return useMutation({
    mutationFn: async (masterclassIds: number[]) => {
      return await api.post('/cart/add-many', { data: masterclassIds });
    },
    onSuccess: () => {
      sendYMEvent(YM_TARGETS_ENUM.SINC_LOCAL_CART);
      // toast({
      //   title: 'Мастер-классы добавлены в корзину',
      //   variant: 'success',
      // });
      clearLocalStorageCart();
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.cartItems] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.masterclasses] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.masterclass] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.cartCount] });
    },
  });
};

export const useRemoveMasterclassFromCartMutation = () => {
  const api = useApi();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (masterclassId: number) => {
      return await api.delete(`/cart/remove/${masterclassId}`);
    },
    onSuccess: () => {
      sendYMEvent(YM_TARGETS_ENUM.DELETE_FROM_CART);
      toast({
        title: 'Мастер-класс удален из корзины',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.cartItems] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.cartCount] });
    },
  });
};

export const useClearCartMutation = (withToast = true) => {
  const api = useApi();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async () => {
      return await api.delete('/cart/clear');
    },
    onSuccess: () => {
      sendYMEvent(YM_TARGETS_ENUM.CLEAR_CART);
      if (withToast) {
        toast({
          title: 'Корзина очищена',
          variant: 'success',
        });
      }
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.cartItems] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.cartCount] });
    },
    onError: () => {
      if (withToast) {
        toast({
          title: 'Не удалось очистить корзину, попробуйте позже',
          variant: 'error',
        });
      }
    },
  });
};

export const useGetCartCountQuery = () => {
  const { token } = useSession();
  const api = useApi();

  return useQuery({
    queryKey: [QUERY_KEYS.cartCount],
    queryFn: async () => {
      return await api.get(`/cart/count`);
    },
    select: (data) => data.data ?? 0,
    enabled: !!token,
  });
};

export const useAddMasterclassesToLocalStorageCartMutation = () => {
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (masterclassId: number) => {
      addToLocalStorageCart(masterclassId);
    },
    onSuccess: () => {
      toast({
        title: 'Мастер-класс добавлен в корзину',
        variant: 'success',
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.localStorageCart],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.localStorageCartCount],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.cartItems],
      });
    },
  });
};

export const useRemoveMasterclassesFromLocalStorageCartMutation = () => {
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (masterclassId: number) => {
      removeFromLocalStorageCart(masterclassId);
    },
    onSuccess: () => {
      toast({
        title: 'Мастер-класс удален из корзины',
        variant: 'success',
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.localStorageCart],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.localStorageCartCount],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.cartItems],
      });
    },
  });
};

export const useClearLocalStorageCartMutation = () => {
  const { toast } = useToast();
  return useMutation({
    mutationFn: async () => {
      clearLocalStorageCart();
    },
    onSuccess: () => {
      toast({
        title: 'Корзина очищена',
        variant: 'success',
      });
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.localStorageCart],
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.localStorageCartCount],
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.cartItems],
        });
      }, 1000);
    },
  });
};

export const useGetLocalStorageCartCountQuery = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.localStorageCartCount],
    queryFn: async () => {
      return getLocalStorageCartCount();
    },
  });
};

export const useGetLocalStorageCartQuery = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.localStorageCart],
    queryFn: async () => {
      return getLocalStorageCart();
    },
  });
};
